import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  copyAllNotesToCustomView,
  copyNoteToCustomView,
  deleteCustomViewNote,
  fetchCustomViewNotes,
  upsertCustomViewNote,
} from '@/api/customViewsApi'
import { reloadCustomViews } from '@/services/customViewsSync'
import { useAppStore } from '@/store/appStore'
import type { ApiCustomViewNote, ContentType, UpsertCustomViewNoteRequest } from '@/types/api'

export const customViewNotesKey = (viewId: number) => ['custom-view-notes', viewId] as const

/**
 * Shared-view notes are collaborative, so the usual 5-minute staleTime is wrong for them: a body
 * held that long is a lost update waiting to happen once the user edits and saves it.
 *
 * Freshness comes from events rather than a timer. `revalidateOn` is any string identifying what
 * the user is currently looking at — when it changes, the cached bodies describe something else
 * and are re-read. Callers should also call `refetch` when switching into the shared scope.
 *
 * All of that stops dead while the editor holds unsaved text. A refetch would replace the note
 * behind the buffer the user is typing into, and no amount of care in the component can undo
 * that once the cache has moved — so the fetch is what gets suppressed, not the symptom. The
 * flag is global to the query key rather than per-consumer because NotesFab subscribes to the
 * same data and would otherwise keep refreshing it underneath the open panel.
 */
export function useCustomViewNotes(viewId: number | null, enabled: boolean, revalidateOn?: string) {
  const queryClient = useQueryClient()
  const notesDirty = useAppStore((s) => s.notesDirty)
  const key = customViewNotesKey(viewId ?? 0)

  const query = useQuery({
    queryKey: key,
    queryFn: () => fetchCustomViewNotes(viewId!),
    enabled: enabled && viewId !== null && !notesDirty,
    staleTime: 0,
    refetchOnMount: notesDirty ? false : 'always',
    refetchOnWindowFocus: !notesDirty,
  })

  const { refetch } = query

  // Navigating to a different practice or practice area is the moment the notes on screen stop
  // being the ones that were fetched, so that is where the re-read belongs.
  useEffect(() => {
    if (revalidateOn === undefined || !enabled || viewId === null || notesDirty) return
    refetch()
  }, [revalidateOn, enabled, viewId, notesDirty, refetch])

  // noteCount lives on the custom view, so any note mutation refreshes the view list too.
  const afterChange = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: key }),
      reloadCustomViews().catch(() => undefined),
    ])
  }

  const save = useMutation({
    mutationFn: (request: UpsertCustomViewNoteRequest) => upsertCustomViewNote(viewId!, request),
    onSuccess: afterChange,
  })

  const remove = useMutation({
    mutationFn: ({ contentType, entityId, expectedRowVersion }: NoteTarget) =>
      deleteCustomViewNote(viewId!, contentType, entityId, expectedRowVersion),
    onSuccess: afterChange,
  })

  const copyFromPersonal = useMutation({
    mutationFn: ({ contentType, entityId }: { contentType: ContentType; entityId: number }) =>
      copyNoteToCustomView(viewId!, { contentType, entityId }),
    onSuccess: afterChange,
  })

  const copyAllFromPersonal = useMutation({
    mutationFn: () => copyAllNotesToCustomView(viewId!),
    onSuccess: afterChange,
  })

  const getNoteFor = (contentType: ContentType, entityId: number): ApiCustomViewNote | null =>
    query.data?.notes.find((n) => n.contentType === contentType && n.entityId === entityId) ?? null

  return {
    notes: query.data?.notes ?? [],
    canEdit: query.data?.canEdit ?? false,
    isOwner: query.data?.isOwner ?? false,
    isLoading: query.isLoading,
    getNoteFor,
    refetch,
    save,
    remove,
    copyFromPersonal,
    copyAllFromPersonal,
  }
}

interface NoteTarget {
  contentType: ContentType
  entityId: number
  expectedRowVersion?: string | null
}
